/**
 * EmptyState (TMMPDM-0034, FEED-002 / ANTI-013): what is empty, why it may be, and what to do
 * next — centred in its region, `body1`. `filtered` is the "nothing matches" variant: it says a
 * filter is on and offers Clear filters (TBL-005). Inside a table it sits in the one full-width
 * cell, so the table keeps its columns and look.
 *
 * TMMPDM-0100: `surface` wraps the block in outlined paper when it sits on the grey page. A
 * table-cell use stays bare (the table is already the surface).
 */
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';

export interface EmptyStateProps {
  title: string;
  body?: string;
  /** The next step — usually the region's create button. */
  action?: ReactNode;
  /** A filter or search is narrowing the region: says so and offers Clear filters. */
  filtered?: { onClear: () => void };
  /** Outlined paper on the grey page. Default false — a table cell stays bare. */
  surface?: boolean;
}

export function EmptyState({ title, body, action, filtered, surface = false }: EmptyStateProps) {
  const { t } = useCopy();
  const stack = (
    <Stack
      spacing={1}
      sx={{ alignItems: 'center', textAlign: 'center', py: surface ? 0 : 2, px: 2 }}
    >
      <Typography variant="body1" component="p" color="text.secondary">
        {title}
      </Typography>
      {body ? (
        <Typography variant="body1" color="text.secondary">
          {body}
        </Typography>
      ) : null}
      {filtered ? (
        <>
          <Typography variant="body1" color="text.secondary">
            {t('components.filteredEmpty', 'A filter is on. Clear it to see everything.')}
          </Typography>
          <Box>
            <Button variant="outlined" onClick={filtered.onClear}>
              {t('components.clearFilters', 'Clear filters')}
            </Button>
          </Box>
        </>
      ) : null}
      {action ? <Box>{action}</Box> : null}
    </Stack>
  );
  return surface ? (
    <Paper variant="outlined" sx={{ py: 4 }}>
      {stack}
    </Paper>
  ) : (
    stack
  );
}
